import React from "react";
import { MediaUpload, BlockControls, InspectorControls } from '@wordpress/block-editor';
import { PanelBody, ToolbarButton, Toolbar, ExternalLink, TextControl, Button, Icon } from '@wordpress/components';
import { __ } from '@wordpress/i18n';

/**
 * Get media URL with specified size
 * @param {Object} media Media object
 * @param {string|null} size Media size
 * @returns {string} Media URL
 */
function getMediaUrl(media, size = null) {
  const src = media?.media_details?.sizes[size];
  return src?.source_url || media.url;
}

/**
 * Universal media handler component for Gutenberg blocks
 * @param {Object} props Component props
 * @param {Object} props.attributes Block attributes
 * @param {Function} props.setAttributes Function to update block attributes
 * @param {string} props.customButtonLabel Custom label for upload button
 * @param {string} props.customRemoveLabel Custom label for remove button
 * @param {Function} props.onMediaSelect Custom callback after media selection
 * @param {Function} props.onMediaRemove Custom callback after media removal
 * @param {React.ReactNode} props.children Child components
 */
const MediaHandler = ({
  attributes,
  setAttributes,
  customButtonLabel = null,
  customRemoveLabel = null,
  onMediaSelect = null,
  onMediaRemove = null,
  children
}) => {
  const { mediaUrl, mediaAlt } = attributes;
  const hasImage = !!mediaUrl;

  const handleMediaSelect = (media) => {
    const newAttributes = {
      mediaUrl: getMediaUrl(media, 'full'),
      mediaAlt: media.alt || ""
    };
    
    setAttributes(newAttributes);
    if (onMediaSelect) {
      onMediaSelect(media, newAttributes);
    }
  };

  const handleMediaRemove = () => {
    const newAttributes = {
      mediaUrl: "",
      mediaAlt: ""
    };
    
    setAttributes(newAttributes);
    if (onMediaRemove) {
      onMediaRemove(newAttributes);
    }
  };

  const handleAltChange = (newAlt) => {
    setAttributes({ mediaAlt: newAlt });
  };

  const buttonLabel = customButtonLabel || __("Wybierz obraz");
  const removeLabel = customRemoveLabel || __("Usuń obraz");

  return (
    <>
      <InspectorControls>
        <PanelBody title={__("Ustawienia obrazu")}>
          <TextControl
            label={__("Tekst alternatywny")}
            value={mediaAlt}
            onChange={handleAltChange}
          />
          <p style={{ fontSize: "10px" }}>
            <ExternalLink href="https://www.w3.org/WAI/tutorials/images/decision-tree">
              {__("Opisz co jest widoczne na obrazie")}
            </ExternalLink>
            <div>{__("Pozostaw puste, jeśli obraz jest czysto dekoracyjny.")}</div>
          </p>
          {hasImage && (
            <>
              <hr />
              <div>
                <Button
                  style={{ color: "#ffffff", background: "#E75244" }}
                  onClick={handleMediaRemove}
                >
                  {removeLabel}
                </Button>
              </div>
            </>
          )}
        </PanelBody>
      </InspectorControls>

      <BlockControls group="other">
        <Toolbar>
          <MediaUpload
            allowedTypes={["image"]}
            onSelect={handleMediaSelect}
            render={({ open }) => (
              <ToolbarButton
                name="image"
                icon={<Icon icon="format-image" />}
                title={buttonLabel}
                onClick={open}
                isActive={hasImage}
              />
            )}
          />
          {hasImage && (
            <ToolbarButton
              name="remove-image"
              icon={<Icon icon="no-alt" />}
              title={removeLabel}
              onClick={handleMediaRemove}
            />
          )}
        </Toolbar>
      </BlockControls>

      {children}
    </>
  );
};

export default MediaHandler; 
